import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreateBillMutation } from '../slices/billsApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const BillsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: customersData, isLoading, error } = useGetCustomersQuery({ keyword: customerSearch });
  const [createBill, { isLoading: savingBills, error: billsError }] = useCreateBillMutation();

  // Filter customers based on search input
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const addToCartHandler = (product, qty) => {
    dispatch(addToCart({ ...product, qty }));
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const placeBillHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const billsData = {
        billItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          images: item.images[0],
          product: item._id,
        })),
        customerDetails: selectedCustomer._id,
        note,
        date: billDate,
      };

      const response = await createBill(billsData).unwrap();
      console.log('Response:', response); // Debugging the backend response
      dispatch(clearCartItems());
      toast.success('Bill successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      console.error('Error:', err); // Log full error object for debugging
      toast.error(err?.data?.message || 'Failed to record bills');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Bills Form</h1>
        
        {cartItems.length === 0 ? (
          <Message>
            Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row>
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Product Thumbnail"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>
                  <Col>{item.name}</Col>
                  <Col md={2}>${item.price}</Col>
                  <Col md={2}>
                    <Form.Control
                      as="select"
                      value={item.qty}
                      onChange={(e) => addToCartHandler(item, Number(e.target.value))}
                    >
                      {[...Array(item.countInStock).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>
                          {x + 1}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                  <Col md={1}>
                    <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)})
                items
              </h2>
              $
              {cartItems
                .reduce((acc, item) => acc + item.qty * item.price, 0)
                .toFixed(2)}
            </ListGroup.Item>
            <ListGroup.Item>
              <h2>Select Customer</h2>
              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message variant="danger">{error?.data?.message || 'Failed to load customers'}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>
                  {filteredCustomers.length > 0 && (
                    <ListGroup className="dropdown-menu mt-2">
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serialNumber}
                      </p>
                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="billDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={billDate}
                          onChange={(e) => setBillDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>
            {billsError && (
              <ListGroup.Item>
                <Message variant="danger">{billsError.message}</Message>
              </ListGroup.Item>
            )}
            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={cartItems.length === 0 || !selectedCustomer}
                onClick={placeBillHandler}
              >
                Submit Bills
              </Button>
              {savingBills && <Loader />}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default BillsForm;